import type { ZodType } from "zod";
import z from "zod";

import { IsoDateSchema, type IsoDateUnbranded } from "@thalamos/common";

import { requiredInvolvementKinds } from "../rules/requiredInvolvementKinds.js";
import {
  AssessmentIdSchema,
  type AssessmentIdUnbranded,
} from "./assessment.js";
import {
  OrganisationIdSchema,
  type OrganisationIdUnbranded,
} from "./organisation.js";
import {
  DateOfBirthSchema,
  PersonIdSchema,
  type DateOfBirthUnbranded,
  type PersonIdUnbranded,
} from "./person.js";
import type { RequiredInvolvement } from "./personState.js";

/**
 * The kinds of work that can be waiting on somebody, in the order they are
 * shown. Which of them a session may have is `waitingWorkGroupsPermittedByRole`'s.
 */
export const WAITING_WORK_GROUPS = [
  "new_assessments",
  "assessments_in_progress",
  "assessments_for_review",
  "unassigned_persons",
] as const;

export type WaitingWorkGroup = (typeof WAITING_WORK_GROUPS)[number];

export const WaitingWorkGroupSchema = z.enum(
  WAITING_WORK_GROUPS,
) satisfies ZodType<WaitingWorkGroup>;

/** How many rows a group shows before anybody asks for more. */
export const WAITING_WORK_FIRST_ROWS = 5;

/** How many more rows each Show more brings. */
export const WAITING_WORK_PAGE_LIMIT = 25;

/**
 * The most rows one request may ask for. A ceiling, because a request names
 * how many rows it wants from the first, and what has accumulated must not
 * size the response.
 */
export const WAITING_WORK_ROWS_LIMIT_MAX = 500;

/** The young person a row is about, as the row names them. */
type WaitingWorkPersonUnbranded = {
  personId: PersonIdUnbranded;
  personName: string;
  dateOfBirth: DateOfBirthUnbranded | null;
};

const waitingWorkPerson = {
  personId: PersonIdSchema,
  personName: z.string(),
  dateOfBirth: DateOfBirthSchema.nullable(),
};

/** A young person whose first definitive assessment is the user's to start. */
export type NewAssessmentRowUnbranded = WaitingWorkPersonUnbranded & {
  /** When the user's live designated-assessor involvement started. */
  designatedAt: Date;
};

export const NewAssessmentRowSchema = z.object({
  ...waitingWorkPerson,
  designatedAt: z.coerce.date(),
}) satisfies ZodType<NewAssessmentRowUnbranded>;

export type NewAssessmentRow = z.infer<typeof NewAssessmentRowSchema>;

/**
 * Where an assessment of the user's stands: returned to them by the decision
 * on its latest attempt, which only a definitive assessment can be, or under
 * way and never yet submitted.
 */
export type AssessmentInProgressStageUnbranded =
  | {
      kind: "returned";
      approverName: string;
      reason: string;
      returnedAt: Date;
    }
  | { kind: "definitive" }
  | { kind: "non_definitive" };

export const AssessmentInProgressStageSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("returned"),
    approverName: z.string(),
    reason: z.string(),
    returnedAt: z.coerce.date(),
  }),
  z.object({ kind: z.literal("definitive") }),
  z.object({ kind: z.literal("non_definitive") }),
]) satisfies ZodType<AssessmentInProgressStageUnbranded>;

export type AssessmentInProgressStage = z.infer<
  typeof AssessmentInProgressStageSchema
>;

/**
 * An assessment of the user's that is theirs to carry on with. How far it has
 * got is the collector's to say, so it is not here.
 */
export type AssessmentInProgressRowUnbranded = WaitingWorkPersonUnbranded & {
  assessmentId: AssessmentIdUnbranded;
  startedAt: Date;
  stage: AssessmentInProgressStageUnbranded;
};

export const AssessmentInProgressRowSchema = z.object({
  ...waitingWorkPerson,
  assessmentId: AssessmentIdSchema,
  startedAt: z.coerce.date(),
  stage: AssessmentInProgressStageSchema,
}) satisfies ZodType<AssessmentInProgressRowUnbranded>;

export type AssessmentInProgressRow = z.infer<
  typeof AssessmentInProgressRowSchema
>;

/** A submitted definitive assessment waiting for the user's decision. */
export type AssessmentForReviewRowUnbranded = WaitingWorkPersonUnbranded & {
  assessmentId: AssessmentIdUnbranded;
  assessorName: string;
  /** The calendar day the latest attempt describes the young person as of. */
  assessedOn: IsoDateUnbranded;
  submittedAt: Date;
  /** Whether this is a second or later attempt, the earlier ones returned. */
  resubmitted: boolean;
};

export const AssessmentForReviewRowSchema = z.object({
  ...waitingWorkPerson,
  assessmentId: AssessmentIdSchema,
  assessorName: z.string(),
  assessedOn: IsoDateSchema,
  submittedAt: z.coerce.date(),
  resubmitted: z.boolean(),
}) satisfies ZodType<AssessmentForReviewRowUnbranded>;

export type AssessmentForReviewRow = z.infer<
  typeof AssessmentForReviewRowSchema
>;

/** An unassigned young person whose case the user runs. */
export type UnassignedPersonRowUnbranded = WaitingWorkPersonUnbranded & {
  /** Never empty: an unassigned person is missing at least one. */
  missing: RequiredInvolvement[];
  /** Whether a definitive assessment is under way while nobody can finish it. */
  assessmentWaiting: boolean;
};

export const UnassignedPersonRowSchema = z.object({
  ...waitingWorkPerson,
  missing: z.array(z.enum(requiredInvolvementKinds)).min(1),
  assessmentWaiting: z.boolean(),
}) satisfies ZodType<UnassignedPersonRowUnbranded>;

export type UnassignedPersonRow = z.infer<typeof UnassignedPersonRowSchema>;

/**
 * Some of one group's rows, in the group's order, and how many it holds in
 * all. Tagged by group, so each carries its own rows and the counts behind
 * its hint.
 */
export type WaitingWorkSliceUnbranded =
  | {
      group: "new_assessments";
      total: number;
      rows: NewAssessmentRowUnbranded[];
    }
  | {
      group: "assessments_in_progress";
      total: number;
      returnedCount: number;
      rows: AssessmentInProgressRowUnbranded[];
    }
  | {
      group: "assessments_for_review";
      total: number;
      resubmittedCount: number;
      rows: AssessmentForReviewRowUnbranded[];
    }
  | {
      group: "unassigned_persons";
      total: number;
      rows: UnassignedPersonRowUnbranded[];
    };

export const WaitingWorkSliceSchema = z.discriminatedUnion("group", [
  z.object({
    group: z.literal("new_assessments"),
    total: z.number().int(),
    rows: z.array(NewAssessmentRowSchema),
  }),
  z.object({
    group: z.literal("assessments_in_progress"),
    total: z.number().int(),
    returnedCount: z.number().int(),
    rows: z.array(AssessmentInProgressRowSchema),
  }),
  z.object({
    group: z.literal("assessments_for_review"),
    total: z.number().int(),
    resubmittedCount: z.number().int(),
    rows: z.array(AssessmentForReviewRowSchema),
  }),
  z.object({
    group: z.literal("unassigned_persons"),
    total: z.number().int(),
    rows: z.array(UnassignedPersonRowSchema),
  }),
]) satisfies ZodType<WaitingWorkSliceUnbranded>;

export type WaitingWorkSlice = z.infer<typeof WaitingWorkSliceSchema>;

/** The slice of one group. */
export type WaitingWorkSliceOf<Group extends WaitingWorkGroup> = Extract<
  WaitingWorkSlice,
  { group: Group }
>;

/**
 * What is waiting on somebody at another organisation they are a member of,
 * counted rather than listed: listing it is that organisation's session's.
 */
export type WaitingWorkElsewhereUnbranded = {
  organisationId: OrganisationIdUnbranded;
  organisationName: string;
  total: number;
};

export const WaitingWorkElsewhereSchema = z.object({
  organisationId: OrganisationIdSchema,
  organisationName: z.string(),
  total: z.number().int(),
}) satisfies ZodType<WaitingWorkElsewhereUnbranded>;

export type WaitingWorkElsewhere = z.infer<typeof WaitingWorkElsewhereSchema>;

/**
 * Everything waiting on the signed-in user where their session acts.
 *
 * `groups` holds a slice of every group the session may have, in
 * `WAITING_WORK_GROUPS` order, including an empty one: an empty group is one
 * that was checked and found clear, which a group the session cannot have is
 * not. `elsewhere` holds each other organisation with anything waiting.
 */
export type WaitingWorkUnbranded = {
  groups: WaitingWorkSliceUnbranded[];
  elsewhere: WaitingWorkElsewhereUnbranded[];
};

export const WaitingWorkSchema = z.object({
  groups: z.array(WaitingWorkSliceSchema),
  elsewhere: z.array(WaitingWorkElsewhereSchema),
}) satisfies ZodType<WaitingWorkUnbranded>;

export type WaitingWork = z.infer<typeof WaitingWorkSchema>;
